/**
 * 绝对路径: D:/XXY/MeiShan_Project/frontend/shared/utils/meal-segment.ts
 * 版本: v2.0
 * 创建日期: 2026-01-29
 * 创建人: AI Assistant
 *
 * 餐段工具函数（前端只消费后端真实 code）
 *
 * 说明：
 * - 输入可能来自历史/旧链接：breakfast/lunch/dinner（或大写 BREAKFAST/LUNCH/DINNER）
 * - 输出必须是后端真实 code（如 ZC0002/WC0003/WC0004）
 * - 仅用于"读取/回显/兼容"，禁止用英文 key 作为业务主键写入 payload
 * - v2.0: 新增统一的餐段业务时序排序函数 sortMealSegments()
 */

export type MealSegmentLegacyKey =
  | 'breakfast'
  | 'lunch'
  | 'dinner'
  | 'afternoon_tea'
  | 'supper'
  | 'BREAKFAST'
  | 'LUNCH'
  | 'DINNER'
  | 'AFTERNOON_TEA'
  | 'SUPPER'

const LEGACY_TO_CODE: Record<string, string> = {
  breakfast: 'ZC0002',
  lunch: 'WC0003',
  dinner: 'WC0004',
  afternoon_tea: 'XWC002',
  supper: 'XY0002',
  BREAKFAST: 'ZC0002',
  LUNCH: 'WC0003',
  DINNER: 'WC0004',
  AFTERNOON_TEA: 'XWC002',
  SUPPER: 'XY0002',
}

/**
 * 归一餐段 code
 * 输入：raw（可能是 legacy key / code / 空值）
 * 输出：后端真实 code（若无法识别则返回原字符串）
 */
export function normalizeMealSegmentCode(raw: unknown): string {
  const v = String(raw ?? '').trim()
  if (!v) return ''
  return LEGACY_TO_CODE[v] || v
}

/**
 * code → 默认中文标签（兜底）
 * 输入：code（后端真实 code）
 * 输出：中文名称；未知则回退为 code
 */
export function mealSegmentDefaultLabel(code: string): string {
  const c = String(code || '').trim()
  if (!c) return ''
  const map: Record<string, string> = {
    ZC0002: '早餐',
    WC0003: '午餐',
    WC0004: '晚餐',
    XWC002: '下午茶',
    XY0002: '宵夜',
  }
  return map[c] || c
}

/**
 * 生成用于样式的 class（避免英文 key 作为 class 主键）
 * 输入：code（后端真实 code）
 * 输出：seg-<CODE> 或空串
 */
export function mealSegmentClass(code: string): string {
  const c = String(code || '').trim()
  return c ? `seg-${c}` : ''
}

// ============================================================
// v2.0: 统一餐段业务时序排序
// ============================================================

/**
 * 5 大标准餐段的业务时序映射（code → 排序权重）
 *
 * 排序依据为一天中的用餐时间先后：
 *   早餐(10) → 午餐(20) → 下午茶(30) → 晚餐(40) → 宵夜(50)
 *
 * 自定义餐段（核销餐、临时餐、加班餐、主题餐等）不在此映射中，
 * 一律使用 CUSTOM_SEGMENT_BASE_PRIORITY(100) 作为第一级权重，
 * 然后按 sort_order（第二级）→ code 字典序（第三级）排列。
 */
export const MEAL_SEGMENT_BUSINESS_PRIORITY: Record<string, number> = {
  ZC0002: 10,   // 早餐
  WC0003: 20,   // 午餐
  XWC002: 30,   // 下午茶
  WC0004: 40,   // 晚餐
  XY0002: 50,   // 宵夜
}

const CUSTOM_SEGMENT_BASE_PRIORITY = 100

/**
 * 通用餐段排序项接口
 * 适配多种数据来源（常量 API、EMS、职场配置等）
 */
export interface MealSegmentSortable {
  code: string
  label?: string
  /** camelCase 格式（经拦截器转换后使用） */
  sortOrder?: number
  /** snake_case 格式（无拦截器时 API 原始返回） */
  sort_order?: number
  /** 透传的原始数据（排序不消费，方便调用方保留完整结构） */
  [key: string]: unknown
}

/**
 * 从常量数据中提取 code → sortOrder 查表
 *
 * 输入: 含 code 和 sortOrder/sort_order 的对象数组（如 useConstants 返回的 options）
 * 输出: Record<code, sortOrder>
 */
export function buildSortOrderMap(
  constants: Array<{ code?: string; value?: string | number; sortOrder?: number; sort_order?: number }>,
): Record<string, number> {
  const map: Record<string, number> = {}
  for (const c of constants) {
    const code = c.code ?? (typeof c.value === 'string' ? c.value : undefined)
    const order = c.sortOrder ?? c.sort_order
    if (code && typeof order === 'number') {
      map[code] = order
    }
  }
  return map
}

/**
 * 统一餐段排序函数（三级排序规则）
 *
 * 排序规则：
 *   L1（第一级）= 业务时序优先级：5 大标准餐段按固定顺序；自定义餐段统一排后
 *   L2（第二级）= sort_order：数字越小越靠前（优先取 item.sortOrder，缺失时从 sortOrderMap 查表）
 *   L3（第三级）= code 字典序：同级 sort_order 时按 code 稳定排序
 *
 * @param items - 待排序的餐段数组（不会修改原数组）
 * @param sortOrderMap - 可选，code → sortOrder 全局查表（来自常量 API），
 *                       当 item 自身没有 sortOrder 时自动查表补全，
 *                       使用 buildSortOrderMap() 从常量数据生成
 * @returns 排序后的新数组
 */
export function sortMealSegments<T extends MealSegmentSortable>(
  items: T[],
  sortOrderMap?: Record<string, number>,
): T[] {
  return [...items].sort((a, b) => {
    const isStdA = a.code in MEAL_SEGMENT_BUSINESS_PRIORITY
    const isStdB = b.code in MEAL_SEGMENT_BUSINESS_PRIORITY

    const l1A = isStdA ? MEAL_SEGMENT_BUSINESS_PRIORITY[a.code] : CUSTOM_SEGMENT_BASE_PRIORITY
    const l1B = isStdB ? MEAL_SEGMENT_BUSINESS_PRIORITY[b.code] : CUSTOM_SEGMENT_BASE_PRIORITY

    if (l1A !== l1B) return l1A - l1B

    const l2A = a.sortOrder ?? a.sort_order ?? sortOrderMap?.[a.code] ?? 999
    const l2B = b.sortOrder ?? b.sort_order ?? sortOrderMap?.[b.code] ?? 999
    if (l2A !== l2B) return l2A - l2B

    return a.code.localeCompare(b.code)
  })
}
